import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { Users, ClipboardList, ShieldCheck, ScrollText, Plus, Trash2, Pencil, Layers, Download, Upload, BarChart3, Scale, CheckCircle2, AlertTriangle, MinusCircle, TrendingUp, ChevronDown, ChevronUp, Save, Copy, History } from "lucide-react";
import InstrumentBuilder from "../components/InstrumentBuilder";
import AnalyticsPanel from "../components/AnalyticsPanel";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
  BarChart, Bar, Legend,
} from "recharts";

const AGREEMENT_META = {
  all_agree:  { label: "All agree",   icon: CheckCircle2,   tone: "bg-primary/10 text-primary border-primary/30" },
  partial:    { label: "Partial",     icon: MinusCircle,    tone: "bg-secondary/40 text-primary border-secondary" },
  disagree:   { label: "Disagree",    icon: AlertTriangle,  tone: "bg-destructive/10 text-destructive border-destructive/40" },
  incomplete: { label: "Incomplete",  icon: MinusCircle,    tone: "bg-muted text-muted-foreground border-border/60" },
};

const OUTCOME_TONE = {
  Normal: "bg-primary/10 text-primary border-primary/30",
  Mild: "bg-secondary/30 text-primary border-secondary",
  Moderate: "bg-[hsl(var(--chart-4)/0.25)] text-foreground border-[hsl(var(--chart-4))]",
  High: "bg-destructive/10 text-destructive border-destructive/40",
  Inconclusive: "bg-muted text-muted-foreground border-border/60",
};

function OutcomePill({ review, label, testId }) {
  if (!review || !review.outcome) {
    return (
      <div className="text-xs text-muted-foreground italic" data-testid={testId}>
        {label} · pending
      </div>
    );
  }
  return (
    <div className="text-xs" data-testid={testId}>
      <div className="uppercase tracking-wider text-[10px] text-muted-foreground mb-1">{label}</div>
      <Badge className={"border text-[11px] " + (OUTCOME_TONE[review.outcome] || "")}>
        {review.outcome}
      </Badge>
    </div>
  );
}

function ReviewerBreakdownChart({ trend }) {
  if (!trend || trend.length === 0) return null;
  // Backend already emits {week, all_agree, partial, disagree, incomplete}
  // per ISO week — recharts just needs a stripped label for the X axis.
  const data = trend.map((t) => ({
    week: t.week.replace(/^\d+-W/, "W"),
    fullWeek: t.week,
    all_agree: t.all_agree || 0,
    partial: t.partial || 0,
    disagree: t.disagree || 0,
    incomplete: t.incomplete || 0,
  }));
  return (
    <div className="rounded-lg border border-border/60 p-4 bg-card mt-3"
         data-testid="comparison-breakdown-chart">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            <BarChart3 size={12} /> Reviewer breakdown
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Per-week counts of All-agree · Partial · Disagree cases (stacked)
          </div>
        </div>
      </div>
      <div style={{ width: "100%", height: 180 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: -12 }}
                    stackOffset="expand">
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="week" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                   axisLine={{ stroke: "hsl(var(--border))" }} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                   axisLine={false} tickLine={false}
                   tickFormatter={(v) => `${Math.round(v * 100)}%`} width={40} />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))",
                              fontSize: 12, background: "hsl(var(--card))" }}
              labelFormatter={(l, p) => p?.[0]?.payload?.fullWeek || l}
              formatter={(v, n) => [`${v} case${v === 1 ? "" : "s"}`, n.replace(/_/g, " ")]}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
            <Bar dataKey="all_agree"  stackId="a" fill="hsl(var(--primary))"        name="All agree" />
            <Bar dataKey="partial"    stackId="a" fill="hsl(var(--secondary))"      name="Partial" />
            <Bar dataKey="disagree"   stackId="a" fill="hsl(var(--destructive))"    name="Disagree" />
            <Bar dataKey="incomplete" stackId="a" fill="hsl(var(--muted-foreground))" fillOpacity={0.4} name="Incomplete" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function TrendChart({ trend }) {
  if (!trend || trend.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground"
           data-testid="comparison-trend-empty">
        Not enough reviewed cases yet to plot a trend — check back once a few
        cases have all three reviews in.
      </div>
    );
  }
  // Recharts handles nulls gracefully; only weeks with reviewed>0 get a point.
  const data = trend.map((t) => ({
    week: t.week.replace(/^\d+-W/, "W"),
    fullWeek: t.week,
    agreement_pct: t.agreement_pct,
    reviewed: t.reviewed,
    total: t.total,
  }));
  const points = data.filter((d) => d.agreement_pct != null).length;
  return (
    <div className="rounded-lg border border-border/60 p-4 bg-card" data-testid="comparison-trend-chart">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            <TrendingUp size={12} /> Agreement % over time
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Reviewed cases per ISO week · last 12 weeks · target 100%
          </div>
        </div>
        <Badge variant="outline" className="text-[10px]" data-testid="comparison-trend-points">
          {points} plotted week{points === 1 ? "" : "s"}
        </Badge>
      </div>
      <div style={{ width: "100%", height: 180 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: -12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="week" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                   axisLine={{ stroke: "hsl(var(--border))" }} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                   axisLine={false} tickLine={false}
                   tickFormatter={(v) => `${v}%`} width={40} />
            <ReferenceLine y={100} stroke="hsl(var(--primary))" strokeDasharray="4 4" strokeOpacity={0.35} />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))",
                              fontSize: 12, background: "hsl(var(--card))" }}
              formatter={(v, _n, p) => [`${v}% (${p?.payload?.reviewed || 0} reviewed)`, "Agreement"]}
              labelFormatter={(l, p) => p?.[0]?.payload?.fullWeek || l}
            />
            <Line type="monotone" dataKey="agreement_pct"
                  stroke="hsl(var(--primary))" strokeWidth={2}
                  dot={{ r: 3, fill: "hsl(var(--primary))" }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ComparisonPanel({ comparison, loading, onRefresh, agreementFilter, setAgreementFilter }) {
  const summary = comparison?.summary || { total: 0, all_agree: 0, partial: 0, disagree: 0, incomplete: 0 };
  const cases = comparison?.cases || [];
  const trend = comparison?.trend || [];
  const filtered = agreementFilter === "all" ? cases : cases.filter((c) => c.agreement === agreementFilter);

  return (
    <div className="space-y-6" data-testid="comparison-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-muted-foreground max-w-xl">
            Side-by-side view of the three independent reviewer tracks. Agreement is
            calculated from the outcomes the three reviewers submitted. Ground-truth
            checking without switching contexts.
          </p>
        </div>
        <Button variant="outline" onClick={onRefresh} disabled={loading}
                className="rounded-full" data-testid="comparison-refresh-btn">
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      <TrendChart trend={trend} />
      <ReviewerBreakdownChart trend={trend} />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <button type="button" onClick={() => setAgreementFilter("all")}
                className={"rounded-lg border p-4 text-left transition-colors " +
                           (agreementFilter === "all" ? "border-primary bg-primary/5" : "border-border/60 hover:bg-muted/40")}
                data-testid="comparison-filter-all">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total cases</div>
          <div className="font-display text-2xl mt-1" data-testid="comparison-summary-total">{summary.total}</div>
        </button>
        {["all_agree", "partial", "disagree", "incomplete"].map((key) => {
          const meta = AGREEMENT_META[key];
          const Icon = meta.icon;
          return (
            <button key={key} type="button" onClick={() => setAgreementFilter(key)}
                    className={"rounded-lg border p-4 text-left transition-colors " +
                               (agreementFilter === key ? "border-primary bg-primary/5" : "border-border/60 hover:bg-muted/40")}
                    data-testid={`comparison-filter-${key}`}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Icon size={11} /> {meta.label}
              </div>
              <div className="font-display text-2xl mt-1" data-testid={`comparison-summary-${key}`}>
                {summary[key] || 0}
              </div>
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground py-6 text-center" data-testid="comparison-loading">
          Loading side-by-side comparison…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-sm text-muted-foreground py-10 text-center rounded-lg border border-dashed border-border/60"
             data-testid="comparison-empty">
          {cases.length === 0
            ? "No cases in the system yet. Assign clinicians and doctors to build a comparison."
            : `No ${AGREEMENT_META[agreementFilter]?.label.toLowerCase() || agreementFilter} cases.`}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-3" data-testid="comparison-list">
          {filtered.map((c) => {
            const agreement = AGREEMENT_META[c.agreement] || AGREEMENT_META.incomplete;
            const Icon = agreement.icon;
            return (
              <Card key={c.case_id} className="mano-card"
                    data-testid={`comparison-row-${c.case_id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge variant="outline" className="font-mono text-[10px]"
                               data-testid={`comparison-case-id-${c.case_id}`}>
                          {c.case_id.slice(0, 8)}
                        </Badge>
                        {c.high_severity && (
                          <Badge className="bg-destructive/10 text-destructive border border-destructive/30 text-[10px]">
                            HIGH
                          </Badge>
                        )}
                        <Badge className={"border text-[10px] " + agreement.tone}
                               data-testid={`comparison-agreement-${c.case_id}`}>
                          <Icon size={10} /> {agreement.label}
                        </Badge>
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          {(c.created_at || "").slice(0, 10)}
                        </span>
                      </div>
                      <div className="text-sm font-medium">
                        {c.respondent?.name || c.respondent?.email || c.respondent?.user_id?.slice(0, 8)}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {c.respondent?.email}
                      </div>
                    </div>
                    {c.final_outcome && (
                      <div className="text-xs">
                        <div className="uppercase tracking-wider text-[10px] text-muted-foreground mb-1">Final outcome</div>
                        <Badge className={"border text-[11px] " + (OUTCOME_TONE[c.final_outcome] || "")}>
                          {c.final_outcome}
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-3 rounded-lg border border-border/60 p-3 bg-muted/30">
                    <OutcomePill review={c.psychology_review} label="Psychology"
                                 testId={`comparison-psychology-${c.case_id}`} />
                    <OutcomePill review={c.psychiatry_review} label="Psychiatry"
                                 testId={`comparison-psychiatry-${c.case_id}`} />
                    <OutcomePill review={c.doctor_review} label="Doctor (independent)"
                                 testId={`comparison-doctor-${c.case_id}`} />
                  </div>

                  {c.doctor_review?.notes && (
                    <div className="mt-3 text-xs text-muted-foreground italic border-l-2 border-primary/40 pl-3"
                         data-testid={`comparison-doctor-notes-${c.case_id}`}>
                      Doctor: "{c.doctor_review.notes}"
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ConsentTimelineDialog({ organization, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/admin/orgs/${encodeURIComponent(organization)}/consent-timeline`)
       .then((r) => setData(r.data))
       .catch(() => toast.error("Could not load consent timeline"))
       .finally(() => setLoading(false));
  }, [open, organization]);

  if (!open) return null;

  const versions = data?.versions || [];
  const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  }) : "—";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose}
         data-testid={`org-consent-dialog-${encodeURIComponent(organization)}`}>
      <div className="bg-card w-full max-w-2xl max-h-[85vh] rounded-2xl border border-border shadow-xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-border/60 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <ScrollText size={11} /> Consent version timeline
            </div>
            <div className="font-display text-lg mt-1 truncate">{organization}</div>
            {data && (
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {data.respondent_count} respondent{data.respondent_count === 1 ? "" : "s"}
                {data.current_active_version_label
                  ? ` · currently active version ${data.current_active_version_label}`
                  : ""}
              </div>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={onClose} className="rounded-full"
                  data-testid="org-consent-close">Close</Button>
        </div>
        <div className="p-5 overflow-y-auto max-h-[70vh]" data-testid="org-consent-list">
          {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
          {!loading && versions.length === 0 && (
            <div className="text-sm text-muted-foreground italic">
              This org's respondents haven't signed a consent version yet.
            </div>
          )}
          {!loading && versions.length > 0 && (
            <ol className="space-y-3">
              {versions.map((v) => {
                const isActive = v.consent_version_id === data?.current_active_version_id;
                return (
                  <li key={v.consent_version_id}
                      className="rounded-lg border border-border/60 p-3"
                      data-testid={`org-consent-row-${v.consent_version_id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px] font-mono">
                        v{v.version || "?"}
                      </Badge>
                      {v.language && (
                        <Badge variant="outline" className="text-[10px] uppercase">{v.language}</Badge>
                      )}
                      {isActive && (
                        <Badge className="text-[10px] border-primary/40 bg-primary/10 text-primary border">
                          currently active
                        </Badge>
                      )}
                      {v.title && (
                        <span className="text-sm font-medium truncate">{v.title}</span>
                      )}
                    </div>
                    <div className="mt-2 grid gap-2 sm:grid-cols-3 text-xs">
                      <div className="rounded bg-muted/40 p-2">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Signed</div>
                        <div className="font-display text-base leading-none mt-0.5">{v.signed_count}</div>
                      </div>
                      <div className="rounded bg-muted/40 p-2">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">First grant</div>
                        <div className="text-[11px] mt-0.5">{fmt(v.first_grant)}</div>
                      </div>
                      <div className="rounded bg-muted/40 p-2">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Last grant</div>
                        <div className="text-[11px] mt-0.5">{fmt(v.last_grant)}</div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function OrgConfigHistoryDialog({ organization, open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/admin/orgs/${encodeURIComponent(organization)}/instrument-config/history`)
       .then((r) => setRows(r.data?.history || []))
       .catch(() => toast.error("Could not load change log"))
       .finally(() => setLoading(false));
  }, [open, organization]);

  if (!open) return null;

  const diff = (fromIds, toIds) => {
    const a = new Set(fromIds || []);
    const b = new Set(toIds || []);
    const added = [...b].filter((x) => !a.has(x)).sort();
    const removed = [...a].filter((x) => !b.has(x)).sort();
    return { added, removed };
  };

  const ACTION_LABEL = {
    set: "Set manually",
    cleared: "Cleared (reset to show all)",
    preset_applied: "Preset applied",
  };

  const csvEscape = (v) => {
    if (v === null || v === undefined) return "";
    const s = Array.isArray(v) ? v.join("|") : String(v);
    // RFC-4180: wrap in quotes if the field has a comma/quote/newline.
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const downloadCsv = () => {
    const header = ["updated_at", "action", "source_preset_name", "updated_by_email",
                    "from_ids", "to_ids", "added", "removed"];
    const body = rows.map((h) => {
      const { added, removed } = diff(h.from_ids, h.to_ids);
      return [
        h.updated_at,
        h.action,
        h.source_preset_name || "",
        h.updated_by_email || h.updated_by || "",
        h.from_ids === null ? "(showing all)" : (h.from_ids || []).join("|"),
        h.to_ids === null ? "(showing all)" : (h.to_ids || []).join("|"),
        added.join("|"),
        removed.join("|"),
      ].map(csvEscape).join(",");
    });
    const blob = new Blob([[header.join(","), ...body].join("\n") + "\n"],
                          { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${organization.replace(/[^\w-]+/g, "_")}_publish_history.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose}
         data-testid={`org-history-dialog-${encodeURIComponent(organization)}`}>
      <div className="bg-card w-full max-w-2xl max-h-[85vh] rounded-2xl border border-border shadow-xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-border/60 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <History size={11} /> Publish list change log
            </div>
            <div className="font-display text-lg mt-1 truncate">{organization}</div>
          </div>
          <Button size="sm" variant="outline" onClick={onClose} className="rounded-full"
                  data-testid="org-history-close">Close</Button>
        </div>
        <div className="px-5 py-2 border-b border-border/50 flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" onClick={downloadCsv}
                  disabled={loading || rows.length === 0}
                  className="rounded-full"
                  data-testid="org-history-download-csv">
            <Download size={13} /> Download CSV
          </Button>
        </div>
        <div className="p-5 overflow-y-auto max-h-[70vh]" data-testid="org-history-list">
          {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-muted-foreground italic">
              No changes recorded yet.
            </div>
          )}
          {!loading && rows.length > 0 && (
            <ol className="space-y-3">
              {rows.map((h) => {
                const { added, removed } = diff(h.from_ids, h.to_ids);
                return (
                  <li key={h.history_id}
                      className="rounded-lg border border-border/60 p-3"
                      data-testid={`org-history-row-${h.history_id}`}>
                    <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground">
                      <Badge variant="outline" className="text-[10px]">
                        {ACTION_LABEL[h.action] || h.action}
                      </Badge>
                      {h.source_preset_name && (
                        <Badge className="border text-[10px] border-primary/40 bg-primary/10 text-primary">
                          preset · {h.source_preset_name}
                        </Badge>
                      )}
                      <span>
                        {new Date(h.updated_at).toLocaleString(undefined, {
                          year: "numeric", month: "short", day: "2-digit",
                          hour: "2-digit", minute: "2-digit",
                        })}
                      </span>
                      <span>· {h.updated_by_email || h.updated_by?.slice(0, 8)}</span>
                    </div>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2 text-xs">
                      <div className="rounded bg-muted/40 p-2">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Before</div>
                        <div className="font-mono text-[11px] break-words">
                          {h.from_ids === null ? "(showing all)"
                            : h.from_ids.length === 0 ? "(none)"
                            : h.from_ids.join(", ")}
                        </div>
                      </div>
                      <div className="rounded bg-muted/40 p-2">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">After</div>
                        <div className="font-mono text-[11px] break-words">
                          {h.to_ids === null ? "(showing all)"
                            : h.to_ids.length === 0 ? "(none)"
                            : h.to_ids.join(", ")}
                        </div>
                      </div>
                    </div>
                    {(added.length > 0 || removed.length > 0) && (
                      <div className="mt-2 flex flex-wrap gap-1 text-[10px]">
                        {added.map((x) => (
                          <span key={"+"+x} className="rounded bg-primary/10 text-primary px-1.5 py-0.5 font-mono">+ {x}</span>
                        ))}
                        {removed.map((x) => (
                          <span key={"-"+x} className="rounded bg-destructive/10 text-destructive px-1.5 py-0.5 font-mono line-through">− {x}</span>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function OrgPublishConfig({ organization, instruments }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null); // null → "showing all" (no config)
  const [history, setHistory] = useState(null);   // {source_preset_name, updated_at, updated_by}
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const orgKey = encodeURIComponent(organization);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/orgs/${orgKey}/instrument-config`);
      setSelected(r.data?.published_instrument_ids ?? null);
      // Provenance surfaces even when the config was applied via preset earlier.
      if (r.data?.updated_at) {
        setHistory({
          source_preset_name: r.data.source_preset_name || null,
          updated_at: r.data.updated_at,
          updated_by: r.data.updated_by || null,
        });
      } else {
        setHistory(null);
      }
    } catch (e) {
      toast.error("Could not load org config");
    } finally {
      setLoading(false);
    }
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && selected === null && !loading) load();
  };

  const toggle = (iid) => {
    setSelected((cur) => {
      const arr = Array.isArray(cur) ? [...cur] : [];
      const i = arr.indexOf(iid);
      if (i >= 0) arr.splice(i, 1);
      else arr.push(iid);
      return arr;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/orgs/${orgKey}/instrument-config`, {
        published_instrument_ids: Array.isArray(selected) ? selected : [],
      });
      toast.success(`Publishable instruments updated for ${organization}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clearConfig = async () => {
    setSaving(true);
    try {
      await api.delete(`/admin/orgs/${orgKey}/instrument-config`);
      setSelected(null);
      setHistory(null);
      toast.success(`Cleared — ${organization} respondents will now see all instruments`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const hasConfig = Array.isArray(selected);
  const filteredInstruments = (instruments || []).filter(
    (i) => i.enabled !== false && i.scoring !== "voice_llm",
  );

  return (
    <div className="mt-4 pt-4 border-t border-border/50"
         data-testid={`org-publish-${orgKey}`}>
      <button type="button" onClick={toggleOpen}
              className="w-full flex items-center justify-between text-left text-sm font-medium hover:text-primary"
              data-testid={`org-publish-toggle-${orgKey}`}>
        <span className="flex items-center gap-2">
          <ClipboardList size={14} />
          Publishable instruments
          {hasConfig && (
            <Badge variant="outline" className="text-[10px]">
              {selected.length} published
            </Badge>
          )}
          {!hasConfig && !loading && (
            <Badge variant="outline" className="text-[10px]">showing all</Badge>
          )}
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid={`org-publish-body-${orgKey}`}>
          {loading ? (
            <div className="text-xs text-muted-foreground">Loading current config…</div>
          ) : (
            <>
              {history && (
                <div className="rounded-lg border border-border/60 bg-muted/30 p-2.5 text-[11px] text-muted-foreground flex items-start gap-1.5 justify-between"
                     data-testid={`org-publish-history-${orgKey}`}>
                  <div className="flex items-start gap-1.5 min-w-0">
                    <History size={12} className="mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      {history.source_preset_name ? (
                        <>Last applied from preset <b className="text-foreground">{history.source_preset_name}</b></>
                      ) : (
                        <>Set manually</>
                      )}
                      <> · {new Date(history.updated_at).toLocaleString(undefined, {
                        year: "numeric", month: "short", day: "2-digit",
                        hour: "2-digit", minute: "2-digit",
                      })}</>
                    </div>
                  </div>
                  <button type="button"
                          onClick={() => setHistoryOpen(true)}
                          className="text-primary hover:underline shrink-0"
                          data-testid={`org-publish-history-open-${orgKey}`}>
                    View change log
                  </button>
                </div>
              )}
              {!history && (
                <div className="flex justify-end">
                  <button type="button"
                          onClick={() => setHistoryOpen(true)}
                          className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
                          data-testid={`org-publish-history-open-${orgKey}`}>
                    <History size={11} /> View change log
                  </button>
                </div>
              )}
              <p className="text-xs text-muted-foreground leading-relaxed">
                Tick the instruments this org's respondents should see. If nothing
                is configured, respondents see every enabled instrument (default).
              </p>
              <div className="grid sm:grid-cols-2 gap-2">
                {filteredInstruments.map((i) => {
                  const on = hasConfig && selected.includes(i.instrument_id);
                  return (
                    <label key={i.instrument_id}
                           className={"flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer transition-colors " +
                                      (on ? "border-primary bg-primary/5" : "border-border/60 hover:bg-muted/40")}
                           data-testid={`org-publish-item-${orgKey}-${i.instrument_id}`}>
                      <input type="checkbox" checked={on}
                             onChange={() => toggle(i.instrument_id)}
                             className="mt-0.5"
                             data-testid={`org-publish-cb-${orgKey}-${i.instrument_id}`} />
                      <span className="min-w-0">
                        <span className="block text-sm truncate">{i.title || i.instrument_id}</span>
                        <span className="block text-[10px] text-muted-foreground uppercase tracking-wider">
                          {i.section?.replace(/_/g, " ") || i.instrument_id}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" onClick={save} disabled={saving}
                        className="rounded-full"
                        data-testid={`org-publish-save-${orgKey}`}>
                  <Save size={13} /> {saving ? "Saving…" : "Save publish list"}
                </Button>
                {hasConfig && (
                  <Button size="sm" variant="outline" onClick={clearConfig} disabled={saving}
                          className="rounded-full"
                          data-testid={`org-publish-clear-${orgKey}`}>
                    Reset — show all
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      )}
      <OrgConfigHistoryDialog organization={organization}
                              open={historyOpen}
                              onClose={() => setHistoryOpen(false)} />
    </div>
  );
}

function PresetImportDialog({ open, onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setText(""); setResult(null); };
  const close = () => { reset(); onClose(); };

  const runImport = async () => {
    setBusy(true); setResult(null);
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch {
      setBusy(false);
      toast.error("Invalid JSON — check the payload and try again");
      return;
    }
    // Accept: a single preset, an array of presets, or an export bundle {presets:[…]}
    let presets;
    if (Array.isArray(parsed)) presets = parsed;
    else if (parsed && Array.isArray(parsed.presets)) presets = parsed.presets;
    else if (parsed && parsed.name && Array.isArray(parsed.instrument_ids)) presets = [parsed];
    else {
      setBusy(false);
      toast.error("JSON must be a preset, an array of presets, or {presets:[…]}");
      return;
    }
    try {
      const r = await api.post("/admin/instrument-presets/import",
                                { presets, on_conflict: "rename" });
      setResult(r.data);
      toast.success(`Imported ${r.data?.created_count ?? 0} preset${r.data?.created_count === 1 ? "" : "s"}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setText(await f.text());
    e.target.value = "";
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={close}
         data-testid="preset-import-dialog">
      <div className="bg-card w-full max-w-2xl max-h-[85vh] rounded-2xl border border-border shadow-xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-border/60 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <Upload size={11} /> Import presets
            </div>
            <div className="font-display text-lg mt-1">Paste JSON or upload a file</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Duplicate names are auto-renamed (e.g. "Mental Health (imported 2)"). Unknown instrument ids are silently dropped.
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={close} className="rounded-full"
                  data-testid="preset-import-close">Close</Button>
        </div>
        <div className="p-5 overflow-y-auto max-h-[70vh] space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-xs inline-flex items-center gap-1 cursor-pointer rounded-full border border-border/60 px-3 py-1.5 hover:bg-muted/40"
                   data-testid="preset-import-file-label">
              <Upload size={12} /> Upload JSON file
              <input type="file" accept="application/json,.json" onChange={onFile}
                     className="hidden" data-testid="preset-import-file-input" />
            </label>
            <div className="text-[11px] text-muted-foreground">or paste below</div>
          </div>
          <Textarea value={text} onChange={(e) => setText(e.target.value)}
                    rows={10}
                    className="font-mono text-[11px]"
                    placeholder='{"presets":[{"name":"Mental Health only","instrument_ids":["PHQ9","GAD7"]}]}'
                    data-testid="preset-import-textarea" />
          <div className="flex justify-end">
            <Button size="sm" onClick={runImport} disabled={busy || !text.trim()}
                    className="rounded-full mano-lift"
                    data-testid="preset-import-run">
              <Save size={13} /> {busy ? "Importing…" : "Import"}
            </Button>
          </div>
          {result && (
            <div className="rounded-lg border border-border/60 p-3 text-xs space-y-1"
                 data-testid="preset-import-result">
              <div><b>{result.created_count}</b> created, <b>{result.skipped_count}</b> skipped</div>
              {(result.created || []).map((p) => (
                <div key={p.preset_id} className="text-muted-foreground">✔ {p.name} · {p.instrument_ids.length} instruments</div>
              ))}
              {(result.skipped || []).map((s, i) => (
                <div key={i} className="text-muted-foreground">– skipped {s.name || "(no name)"} · {s.reason}</div>
              ))}
              {Object.entries(result.dropped_instrument_ids || {}).map(([n, ids]) => (
                <div key={n} className="text-destructive">! {n}: dropped unknown ids {ids.join(", ")}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InstrumentPresetsPanel({ instruments, organizations }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [draft, setDraft] = useState({ name: "", instrument_ids: [] });
  const [applyState, setApplyState] = useState({}); // {preset_id: {open, orgs:[]}}
  const [busy, setBusy] = useState(false);

  const filteredInstruments = (instruments || []).filter(
    (i) => i.enabled !== false && i.scoring !== "voice_llm",
  );

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/instrument-presets");
      setPresets(r.data?.presets || []);
    } catch (e) {
      toast.error("Could not load presets");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleDraft = (iid) => {
    setDraft((d) => {
      const arr = d.instrument_ids.includes(iid)
        ? d.instrument_ids.filter((x) => x !== iid)
        : [...d.instrument_ids, iid];
      return { ...d, instrument_ids: arr };
    });
  };

  const duplicate = (preset) => {
    // Prefill the create form with the source preset's instruments and a
    // "(copy)" name — the admin can tweak either before saving.
    setDraft({
      name: `${preset.name} (copy)`,
      instrument_ids: [...(preset.instrument_ids || [])],
    });
    setCreateOpen(true);
    // Scroll the form into view so the user sees the prefill.
    setTimeout(() => {
      document.querySelector('[data-testid="preset-create-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    toast.success(`Prefilled from "${preset.name}" — tweak & save`);
  };

  const exportPreset = (preset) => {
    // Preview→production copy-paste bundle. Version stamp lets us evolve the
    // shape later without confusing older imports.
    const bundle = {
      manodayam_preset_export_version: 1,
      exported_at: new Date().toISOString(),
      presets: [{
        preset_id: preset.preset_id,
        name: preset.name,
        instrument_ids: [...(preset.instrument_ids || [])],
      }],
    };
    const blob = new Blob([JSON.stringify(bundle, null, 2)],
                          { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `manodayam_preset_${preset.name.replace(/[^\w-]+/g, "_")}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported "${preset.name}"`);
  };

  const exportAll = () => {
    if (presets.length === 0) { toast.error("Nothing to export"); return; }
    const bundle = {
      manodayam_preset_export_version: 1,
      exported_at: new Date().toISOString(),
      presets: presets.map((p) => ({
        preset_id: p.preset_id,
        name: p.name,
        instrument_ids: [...(p.instrument_ids || [])],
      })),
    };
    const blob = new Blob([JSON.stringify(bundle, null, 2)],
                          { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `manodayam_presets_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${presets.length} preset${presets.length === 1 ? "" : "s"}`);
  };

  const create = async () => {
    if (!draft.name.trim()) { toast.error("Preset name is required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/instrument-presets", draft);
      toast.success(`Preset "${draft.name}" created`);
      setDraft({ name: "", instrument_ids: [] });
      setCreateOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (preset) => {
    if (!window.confirm(`Delete preset "${preset.name}"? Orgs already using it keep their current config.`)) return;
    try {
      await api.delete(`/admin/instrument-presets/${preset.preset_id}`);
      toast.success("Preset deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const openApply = (preset) => {
    setApplyState((s) => ({ ...s, [preset.preset_id]: { open: true, orgs: [] } }));
  };
  const toggleApplyOrg = (preset, org) => {
    setApplyState((s) => {
      const cur = s[preset.preset_id] || { open: true, orgs: [] };
      const orgs = cur.orgs.includes(org)
        ? cur.orgs.filter((x) => x !== org)
        : [...cur.orgs, org];
      return { ...s, [preset.preset_id]: { ...cur, orgs } };
    });
  };
  const applyPreset = async (preset) => {
    const orgs = applyState[preset.preset_id]?.orgs || [];
    if (orgs.length === 0) { toast.error("Pick at least one organization"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/instrument-presets/${preset.preset_id}/apply`,
                                { organizations: orgs });
      toast.success(`Applied to ${r.data?.count || orgs.length} organization${orgs.length === 1 ? "" : "s"}`);
      setApplyState((s) => ({ ...s, [preset.preset_id]: { open: false, orgs: [] } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mano-card" data-testid="presets-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="font-display text-lg flex items-center gap-2">
              <Layers size={16} /> Publish presets
            </div>
            <p className="text-xs text-muted-foreground mt-1 max-w-lg">
              Save a curated instrument set once, apply it to many orgs in one click.
              Applying overwrites each org's current publish list.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={exportAll}
                    disabled={presets.length === 0}
                    className="rounded-full"
                    data-testid="preset-export-all-btn">
              <Download size={13} /> Export all
            </Button>
            <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}
                    className="rounded-full"
                    data-testid="preset-import-btn">
              <Upload size={13} /> Import
            </Button>
            <Button size="sm" onClick={() => setCreateOpen((v) => !v)}
                    className="rounded-full" data-testid="preset-new-btn">
              <Plus size={13} /> {createOpen ? "Cancel" : "New preset"}
            </Button>
          </div>
        </div>

        {createOpen && (
          <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3"
               data-testid="preset-create-form">
            <div>
              <Label htmlFor="preset-name">Preset name</Label>
              <Input id="preset-name" value={draft.name}
                     onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                     placeholder='e.g. "Mental Health only"'
                     data-testid="preset-name-input" />
            </div>
            <div>
              <Label>Instruments in this preset</Label>
              <div className="mt-2 grid sm:grid-cols-2 gap-2">
                {filteredInstruments.map((i) => {
                  const on = draft.instrument_ids.includes(i.instrument_id);
                  return (
                    <label key={i.instrument_id}
                           className={"flex items-start gap-2 rounded-lg border p-2 cursor-pointer text-sm " +
                                      (on ? "border-primary bg-primary/10" : "border-border/60 hover:bg-muted/40")}
                           data-testid={`preset-draft-item-${i.instrument_id}`}>
                      <input type="checkbox" checked={on}
                             onChange={() => toggleDraft(i.instrument_id)}
                             className="mt-0.5" />
                      <span className="min-w-0">
                        <span className="block truncate">{i.title || i.instrument_id}</span>
                        <span className="block text-[10px] text-muted-foreground uppercase tracking-wider">
                          {i.section?.replace(/_/g, " ") || i.instrument_id}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            <Button size="sm" onClick={create} disabled={busy}
                    className="rounded-full mano-lift" data-testid="preset-create-save">
              <Save size={13} /> {busy ? "Saving…" : "Create preset"}
            </Button>
          </div>
        )}

        <div className="mt-4 space-y-3" data-testid="presets-list">
          {loading && <div className="text-xs text-muted-foreground">Loading presets…</div>}
          {!loading && presets.length === 0 && (
            <div className="text-xs text-muted-foreground italic">
              No presets yet. Create one above to bulk-apply an instrument set to many orgs.
            </div>
          )}
          {presets.map((p) => {
            const ap = applyState[p.preset_id] || { open: false, orgs: [] };
            return (
              <div key={p.preset_id}
                   className="rounded-lg border border-border/60 p-3"
                   data-testid={`preset-row-${p.preset_id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {(p.instrument_ids || []).length} instrument{(p.instrument_ids || []).length === 1 ? "" : "s"} · {(p.instrument_ids || []).join(", ") || "empty"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => openApply(p)}
                            className="rounded-full"
                            data-testid={`preset-apply-open-${p.preset_id}`}>
                      Apply to orgs
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => exportPreset(p)}
                            className="rounded-full"
                            data-testid={`preset-export-${p.preset_id}`}
                            title="Export this preset as JSON">
                      <Download size={13} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => duplicate(p)}
                            className="rounded-full"
                            data-testid={`preset-duplicate-${p.preset_id}`}
                            title="Duplicate this preset">
                      <Copy size={13} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(p)}
                            className="rounded-full text-destructive hover:text-destructive"
                            data-testid={`preset-delete-${p.preset_id}`}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </div>
                {ap.open && (
                  <div className="mt-3 pt-3 border-t border-border/50"
                       data-testid={`preset-apply-body-${p.preset_id}`}>
                    <div className="text-[11px] text-muted-foreground mb-2">
                      Pick the organizations to overwrite:
                    </div>
                    {organizations.length === 0 ? (
                      <div className="text-xs text-muted-foreground italic">
                        No orgs yet — respondents need to sign up with an org first.
                      </div>
                    ) : (
                      <div className="grid sm:grid-cols-2 gap-1.5">
                        {organizations.map((org) => {
                          const on = ap.orgs.includes(org);
                          return (
                            <label key={org}
                                   className={"flex items-center gap-2 rounded border p-2 cursor-pointer text-xs " +
                                              (on ? "border-primary bg-primary/10" : "border-border/60 hover:bg-muted/40")}
                                   data-testid={`preset-apply-org-${p.preset_id}-${encodeURIComponent(org)}`}>
                              <input type="checkbox" checked={on}
                                     onChange={() => toggleApplyOrg(p, org)} />
                              <span className="truncate">{org}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" onClick={() => applyPreset(p)} disabled={busy || ap.orgs.length === 0}
                              className="rounded-full mano-lift"
                              data-testid={`preset-apply-run-${p.preset_id}`}>
                        {busy ? "Applying…" : `Apply to ${ap.orgs.length} org${ap.orgs.length === 1 ? "" : "s"}`}
                      </Button>
                      <Button size="sm" variant="outline"
                              onClick={() => setApplyState((s) => ({ ...s, [p.preset_id]: { open: false, orgs: [] } }))}
                              className="rounded-full">
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <PresetImportDialog open={importOpen}
                            onClose={() => setImportOpen(false)}
                            onDone={load} />
      </CardContent>
    </Card>
  );
}

function OrgConsentTimelineLauncher({ organization }) {
  const [open, setOpen] = useState(false);
  const orgKey = encodeURIComponent(organization);
  return (
    <div className="mt-3 pt-3 border-t border-border/50 flex justify-end">
      <button type="button"
              onClick={() => setOpen(true)}
              className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
              data-testid={`org-consent-open-${orgKey}`}>
        <ScrollText size={11} /> Consent version timeline
      </button>
      <ConsentTimelineDialog organization={organization}
                              open={open}
                              onClose={() => setOpen(false)} />
    </div>
  );
}

function OrganizationsPanel({ insights, loading, onRefresh, instruments }) {
  const orgs = insights?.organizations || [];
  const totalRespondents = insights?.total_respondents || 0;
  const totalResponses = insights?.total_responses || 0;
  const orgNames = orgs.map((o) => o.organization).filter((n) => n && n !== "(Unassigned)");

  // Build a stable colour palette per band. `bands` is a free-form dict so we
  // fall back gracefully for anything unexpected (e.g. Inconclusive).
  const BAND_TONE = {
    Normal:      "bg-primary/70",
    Mild:        "bg-secondary",
    Moderate:    "bg-[hsl(var(--chart-4))]",
    "Moderately severe depression": "bg-[hsl(var(--chart-4))]",
    High:        "bg-destructive",
    Severe:      "bg-destructive",
    Inconclusive:"bg-muted-foreground/50",
  };
  const toneFor = (band) => BAND_TONE[band] || "bg-muted-foreground/40";

  return (
    <div className="space-y-6" data-testid="organizations-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-muted-foreground max-w-xl">
            Respondent counts and band distribution per organization. Rows
            without an org tag land in the <b>(Unassigned)</b> bucket at the
            bottom — nudge those respondents on their next visit to add one.
          </p>
        </div>
        <Button variant="outline" onClick={onRefresh} disabled={loading}
                className="rounded-full" data-testid="organizations-refresh-btn">
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      <InstrumentPresetsPanel instruments={instruments} organizations={orgNames} />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="rounded-lg border border-border/60 p-4" data-testid="organizations-summary-orgs">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Organizations</div>
          <div className="font-display text-2xl mt-1">{orgs.length}</div>
        </div>
        <div className="rounded-lg border border-border/60 p-4" data-testid="organizations-summary-respondents">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Respondents</div>
          <div className="font-display text-2xl mt-1">{totalRespondents}</div>
        </div>
        <div className="rounded-lg border border-border/60 p-4" data-testid="organizations-summary-responses">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total responses</div>
          <div className="font-display text-2xl mt-1">{totalResponses}</div>
        </div>
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground py-6 text-center"
             data-testid="organizations-loading">
          Loading segment health…
        </div>
      )}
      {!loading && orgs.length === 0 && (
        <div className="text-sm text-muted-foreground py-10 text-center rounded-lg border border-dashed border-border/60"
             data-testid="organizations-empty">
          No respondents yet. Once someone signs up with an organization, they'll show up here.
        </div>
      )}

      {!loading && orgs.length > 0 && (
        <div className="space-y-3" data-testid="organizations-list">
          {orgs.map((o) => {
            const bandEntries = Object.entries(o.bands || {});
            const bandTotal = bandEntries.reduce((s, [, v]) => s + (v || 0), 0);
            const isUnassigned = o.organization === "(Unassigned)";
            return (
              <Card key={o.organization} className="mano-card"
                    data-testid={`organizations-row-${encodeURIComponent(o.organization)}`}>
                <CardContent className="p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-display text-lg truncate flex items-center gap-2">
                        {o.organization}
                        {isUnassigned && (
                          <Badge variant="outline" className="text-[10px]">no org</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1"
                           data-testid={`organizations-row-${encodeURIComponent(o.organization)}-counts`}>
                        {o.respondent_count} respondent{o.respondent_count === 1 ? "" : "s"}
                        {" · "}
                        {o.responded_count} responded
                        {" · "}
                        {o.response_count} response{o.response_count === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>

                  {bandTotal > 0 ? (
                    <>
                      <div className="mt-4 flex h-3 w-full overflow-hidden rounded-full border border-border/60"
                           data-testid={`organizations-row-${encodeURIComponent(o.organization)}-bar`}
                           title="Band distribution">
                        {bandEntries.map(([band, count]) => (
                          <div key={band}
                               className={toneFor(band)}
                               style={{ width: `${(count / bandTotal) * 100}%` }}
                               title={`${band}: ${count}`} />
                        ))}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                        {bandEntries.map(([band, count]) => (
                          <span key={band} className="inline-flex items-center gap-1">
                            <span className={"inline-block h-2 w-2 rounded-full " + toneFor(band)} />
                            {band} · {count}
                          </span>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="mt-3 text-xs text-muted-foreground italic">
                      No scored responses yet.
                    </div>
                  )}

                  {/* Publishable instruments — checklist per org */}
                  <OrgPublishConfig organization={o.organization}
                                    instruments={instruments} />
                  <OrgConsentTimelineLauncher organization={o.organization} />
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [instruments, setInstruments] = useState([]);
  const [respondents, setRespondents] = useState([]);
  const [consentVersions, setConsentVersions] = useState([]);
  const [audit, setAudit] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [newProfession, setNewProfession] = useState("");
  const [showResults, setShowResults] = useState(true);
  const [tieBreaker, setTieBreaker] = useState(true);
  const [users, setUsers] = useState([]);
  const [cases, setCases] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [sites, setSites] = useState([]);
  const [newCohort, setNewCohort] = useState({ name: "", description: "" });
  const [newSite, setNewSite] = useState({ name: "", description: "" });
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderInitial, setBuilderInitial] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [newConsent, setNewConsent] = useState({ version: "", language: "en", title: "", body: "" });
  const [comparison, setComparison] = useState({ cases: [], summary: null });
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [agreementFilter, setAgreementFilter] = useState("all");
  const [orgInsights, setOrgInsights] = useState(null);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  const loadOrgInsights = async () => {
    setLoadingOrgs(true);
    try {
      const res = await api.get("/admin/organizations/insights");
      setOrgInsights(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load organization insights");
    } finally {
      setLoadingOrgs(false);
    }
  };

  const loadComparison = async () => {
    setLoadingComparison(true);
    try {
      const res = await api.get("/admin/case-comparison");
      setComparison(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load comparison");
    } finally {
      setLoadingComparison(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user || !["PLATFORM_ADMIN", "CONTENT_ADMIN"].includes(user.role)) {
      nav("/admin/login");
      return;
    }
    loadAll();
  }, [user, loading, nav]);

  const loadAll = async () => {
    try {
      const [i, r, cv, al, p, s, tb, u, k, co, si] = await Promise.all([
        api.get("/admin/instruments"),
        api.get("/admin/respondents"),
        api.get("/admin/consent-versions"),
        api.get("/admin/audit-logs", { params: { limit: 100 } }),
        api.get("/professions"),
        api.get("/admin/settings"),
        api.get("/admin/settings/tiebreaker"),
        api.get("/admin/users"),
        api.get("/admin/cases"),
        api.get("/admin/cohorts"),
        api.get("/admin/sites"),
      ]);
      setInstruments(i.data);
      setRespondents(r.data);
      setConsentVersions(cv.data);
      setAudit(al.data);
      setProfessions(p.data);
      setShowResults(s.data.show_results_to_respondent);
      setTieBreaker(tb.data.psychiatrist_tie_breaker_enabled);
      setUsers(u.data);
      setCases(k.data);
      setCohorts(co.data);
      setSites(si.data);
      // Lazy comparison + orgs load — fire and forget, don't block admin dashboard.
      loadComparison();
      loadOrgInsights();
    } catch (e) {
      toast.error("Failed to load admin data");
    }
  };

  const toggleInstrument = async (instrument_id, enabled) => {
    try {
      await api.patch(`/admin/instruments/${instrument_id}`, { enabled });
      setInstruments((list) => list.map((x) => x.instrument_id === instrument_id ? { ...x, enabled } : x));
      toast.success(`${instrument_id} ${enabled ? "enabled" : "disabled"}`);
    } catch { toast.error("Update failed"); }
  };

  const toggleResults = async (v) => {
    try {
      await api.patch("/admin/settings/result-visibility", { show_results_to_respondent: v });
      setShowResults(v);
      toast.success(`Results ${v ? "visible" : "hidden"} to respondents`);
    } catch { toast.error("Update failed"); }
  };

  const toggleTieBreaker = async (v) => {
    try {
      await api.patch("/admin/settings/tiebreaker", { enabled: v });
      setTieBreaker(v);
      toast.success(`Psychiatrist tie-breaker ${v ? "enabled" : "disabled"}`);
    } catch { toast.error("Update failed"); }
  };

  const changeRole = async (user_id, role) => {
    try {
      await api.patch(`/admin/users/${user_id}/role`, { role });
      setUsers((list) => list.map((x) => x.user_id === user_id ? { ...x, role } : x));
      toast.success("Role updated");
    } catch { toast.error("Update failed"); }
  };

  const changeUserScope = async (user_id, patch) => {
    try {
      const body = {
        cohort_id: patch.cohort_id === undefined ? undefined : (patch.cohort_id || ""),
        site_id: patch.site_id === undefined ? undefined : (patch.site_id || ""),
      };
      await api.patch(`/admin/users/${user_id}/scope`, body);
      setUsers((list) => list.map((x) => x.user_id === user_id ? { ...x, ...patch } : x));
      toast.success("Scope updated");
    } catch { toast.error("Update failed"); }
  };

  const addCohort = async () => {
    if (!newCohort.name.trim()) { toast.error("Cohort name required"); return; }
    try {
      const res = await api.post("/admin/cohorts", newCohort);
      setCohorts((l) => [res.data, ...l]);
      setNewCohort({ name: "", description: "" });
      toast.success("Cohort added");
    } catch { toast.error("Add failed"); }
  };

  const removeCohort = async (id) => {
    if (!confirm("Delete this cohort? Respondents & instruments will be unassigned from it.")) return;
    try {
      await api.delete(`/admin/cohorts/${id}`);
      setCohorts((l) => l.filter((c) => c.cohort_id !== id));
      loadAll();
      toast.success("Cohort deleted");
    } catch { toast.error("Delete failed"); }
  };

  const addSite = async () => {
    if (!newSite.name.trim()) { toast.error("Site name required"); return; }
    try {
      const res = await api.post("/admin/sites", newSite);
      setSites((l) => [res.data, ...l]);
      setNewSite({ name: "", description: "" });
      toast.success("Site added");
    } catch { toast.error("Add failed"); }
  };

  const removeSite = async (id) => {
    if (!confirm("Delete this site? Respondents & instruments will be unassigned from it.")) return;
    try {
      await api.delete(`/admin/sites/${id}`);
      setSites((l) => l.filter((s) => s.site_id !== id));
      loadAll();
      toast.success("Site deleted");
    } catch { toast.error("Delete failed"); }
  };

  const openNewInstrument = () => { setBuilderInitial(null); setBuilderOpen(true); };
  const openEditInstrument = (inst) => { setBuilderInitial(inst); setBuilderOpen(true); };
  const deleteInstrument = async (instrument_id) => {
    if (!confirm(`Delete instrument ${instrument_id}? This does not remove past responses but the instrument will vanish from the flow.`)) return;
    try {
      await api.delete(`/admin/instruments/${instrument_id}`);
      setInstruments((l) => l.filter((i) => i.instrument_id !== instrument_id));
      toast.success("Instrument deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const importInputRef = useRef(null);

  const exportInstrument = async (inst) => {
    try {
      const res = await api.get(`/admin/instruments/${inst.instrument_id}/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inst.instrument_id}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${inst.instrument_id}.json`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
  };

  // Downloads a ZIP bundle for the instrument:
  //   {instrument_id}_responses.xlsx  → Responses + Voice_Samples sheets
  //   voice_samples/{sample_id}.{ext} → actual audio blobs
  // The audio inlining can take a few seconds so we surface a "Preparing…"
  // toast while the response streams.
  const exportInstrumentBundle = async (inst) => {
    // Pick an organization scope. Empty string means "all respondents".
    let orgs = [];
    try {
      const r = await api.get("/admin/organizations");
      orgs = r.data?.organizations || [];
    } catch { /* fall through — export still works without org filter */ }
    const optionsText = orgs.length
      ? `Type an organization to filter by, or leave blank for all.\n\nKnown organizations:\n${orgs.map((o, i) => `${i + 1}. ${o}`).join("\n")}`
      : "Type an organization to filter by, or leave blank for all.";
    const orgInput = window.prompt(optionsText, "");
    if (orgInput === null) return; // cancelled
    const org = orgInput.trim();
    const t = toast.loading(
      org ? `Preparing ${inst.instrument_id} bundle for ${org}…`
          : `Preparing bundle for ${inst.instrument_id}…`,
    );
    try {
      const res = await api.get(
        `/admin/instruments/${inst.instrument_id}/export.zip`,
        { responseType: "blob", params: org ? { organization: org } : {} },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const suffix = org ? `_${org.replace(/\s+/g, "_")}` : "";
      a.download = `${inst.instrument_id}${suffix}_bundle.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(`Bundle downloaded — ${a.download}`, { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bundle export failed", { id: t });
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const res = await api.post("/admin/instruments/import", data);
      toast.success(`Imported ${res.data.instrument_id} (disabled by default)`);
      loadAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || (err instanceof SyntaxError ? "Invalid JSON file" : "Import failed");
      toast.error(msg);
    }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await api.post("/admin/notifications/send-digest", { days: 7 });
      if (res.data.ok) {
        toast.success(`Digest sent to ${res.data.recipient}`);
      } else {
        toast.error("Email provider unavailable — check backend logs");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSendingDigest(false); }
  };

  const saveProfessions = async (items) => {
    try {
      await api.put("/admin/professions", { items });
      setProfessions(items);
      toast.success("Professions updated");
    } catch { toast.error("Update failed"); }
  };

  const addProfession = () => {
    const v = newProfession.trim();
    if (!v || professions.includes(v)) return;
    const next = [...professions, v];
    saveProfessions(next);
    setNewProfession("");
  };

  const removeProfession = (p) => {
    const next = professions.filter((x) => x !== p);
    saveProfessions(next);
  };

  const publishConsent = async () => {
    if (!newConsent.version || !newConsent.title || !newConsent.body) {
      toast.error("Fill version, title, body");
      return;
    }
    try {
      await api.post("/admin/consent-versions", {
        ...newConsent,
        purposes: [
          { key: "assessment", label: "Use my data for this assessment", required: true },
          { key: "sharing", label: "Share with assigned clinicians", required: true },
          { key: "research", label: "De-identified research use (optional)", required: false },
        ],
      });
      toast.success("New consent version published");
      setNewConsent({ version: "", language: "en", title: "", body: "" });
      loadAll();
    } catch { toast.error("Publish failed"); }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background">
        <BrandHeader />
        <div className="mano-container py-20 text-muted-foreground">Loading admin…</div>
      </div>
    );
  }

  const stats = [
    { icon: Users, label: "Respondents", value: respondents.length },
    { icon: ClipboardList, label: "Instruments", value: instruments.length },
    { icon: ShieldCheck, label: "Consent Versions", value: consentVersions.length },
    { icon: ScrollText, label: "Audit Events", value: audit.length },
  ];

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container py-10">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Admin Console</div>
            <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight mt-1">Manodayam Control Center</h1>
            <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-1">
              <span>Signed in as</span>
              <span className="font-medium">{user.email}</span>
              <span>· Role:</span>
              <Badge variant="outline" className="ml-1">{user.role}</Badge>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10">
          {stats.map((s, i) => (
            <Card key={i} className="mano-card">
              <CardContent className="p-5">
                <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-secondary/40 text-primary">
                  <s.icon size={16} />
                </div>
                <div className="text-xs text-muted-foreground mt-3 uppercase tracking-wider">{s.label}</div>
                <div className="font-display text-2xl mt-1" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>{s.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="instruments" className="w-full">
          <TabsList className="mb-6 flex-wrap h-auto" data-testid="admin-tabs">
            <TabsTrigger value="instruments" data-testid="tab-instruments">Instruments</TabsTrigger>
            <TabsTrigger value="respondents" data-testid="tab-respondents">Respondents</TabsTrigger>
            <TabsTrigger value="cases" data-testid="tab-cases">Cases</TabsTrigger>
            <TabsTrigger value="comparison" data-testid="tab-comparison">Comparison</TabsTrigger>
            <TabsTrigger value="organizations" data-testid="tab-organizations">Organizations</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users & Roles</TabsTrigger>
            <TabsTrigger value="scope" data-testid="tab-scope">Cohorts & Sites</TabsTrigger>
            <TabsTrigger value="consent" data-testid="tab-consent">Consent</TabsTrigger>
            <TabsTrigger value="professions" data-testid="tab-professions">Professions</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>
          </TabsList>

          <TabsContent value="instruments">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <p className="text-sm text-muted-foreground">
                {instruments.length} instruments · Toggle to enable/disable · Click Edit for the builder
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept=".json,application/json"
                  ref={importInputRef}
                  onChange={handleImportFile}
                  className="hidden"
                  data-testid="instrument-import-input"
                />
                <Button variant="outline" onClick={() => importInputRef.current?.click()}
                  className="rounded-full" data-testid="instrument-import-btn">
                  <Upload size={14} /> Import JSON
                </Button>
                <Button onClick={openNewInstrument} className="rounded-full mano-lift" data-testid="instrument-new-btn">
                  <Plus size={14} /> New instrument
                </Button>
              </div>
            </div>
            <div className="grid gap-4">
              {instruments.map((i) => {
                const scoped = (i.cohort_ids?.length || 0) + (i.site_ids?.length || 0) > 0;
                return (
                  <Card key={i.instrument_id} className="mano-card">
                    <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
                      <div className="min-w-0 max-w-xl">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" data-testid={`instrument-id-${i.instrument_id}`}>{i.instrument_id}</Badge>
                          <span className="text-xs text-muted-foreground">{(i.section || "").replace(/_/g, " ")}</span>
                          {scoped ? (
                            <Badge className="bg-secondary/40 text-primary border border-secondary text-[10px]">
                              <Layers size={10} /> Scoped · {i.cohort_ids?.length || 0}C / {i.site_ids?.length || 0}S
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px]">Global</Badge>
                          )}
                        </div>
                        <div className="font-display font-semibold text-lg mt-1">{i.title}</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {i.items?.length || 0} items · {i.cutoffs?.length || 0} bands · scoring: {i.scoring}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button size="sm" variant="ghost" onClick={() => exportInstrumentBundle(i)}
                          className="rounded-full" data-testid={`instrument-bundle-${i.instrument_id}`}
                          title="Download responses + voice samples as a ZIP">
                          <Download size={12} /> Bundle
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => exportInstrument(i)}
                          className="rounded-full" data-testid={`instrument-export-${i.instrument_id}`}>
                          <Download size={12} /> Export
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEditInstrument(i)}
                          className="rounded-full" data-testid={`instrument-edit-${i.instrument_id}`}>
                          <Pencil size={12} /> Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteInstrument(i.instrument_id)}
                          className="rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive"
                          data-testid={`instrument-delete-${i.instrument_id}`}>
                          <Trash2 size={12} /> Delete
                        </Button>
                        <div className="flex items-center gap-2 pl-2 border-l border-border/60">
                          <span className="text-xs text-muted-foreground">{i.enabled ? "Enabled" : "Disabled"}</span>
                          <Switch
                            checked={!!i.enabled}
                            onCheckedChange={(v) => toggleInstrument(i.instrument_id, v)}
                            data-testid={`instrument-toggle-${i.instrument_id}`}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="respondents">
            <Card className="mano-card">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-3 font-medium">Name</th>
                      <th className="p-3 font-medium">Email</th>
                      <th className="p-3 font-medium">Profession</th>
                      <th className="p-3 font-medium">Consent</th>
                      <th className="p-3 font-medium">Responses</th>
                      <th className="p-3 font-medium">Voice</th>
                      <th className="p-3 font-medium">Registered</th>
                    </tr>
                  </thead>
                  <tbody data-testid="respondents-table">
                    {respondents.map((r) => (
                      <tr key={r.user_id} className="border-t border-border/60">
                        <td className="p-3">{r.name}</td>
                        <td className="p-3 text-muted-foreground">{r.email}</td>
                        <td className="p-3">{r.profession}{r.profession_other ? ` · ${r.profession_other}` : ""}</td>
                        <td className="p-3">
                          {r.consent_active ? <Badge className="bg-primary/10 text-primary border border-primary/30">Active</Badge> : <Badge variant="outline">None</Badge>}
                        </td>
                        <td className="p-3">{r.response_count}</td>
                        <td className="p-3">{r.voice_count}</td>
                        <td className="p-3 text-muted-foreground">{(r.created_at || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                    {respondents.length === 0 && (
                      <tr><td colSpan="7" className="p-6 text-center text-muted-foreground">No respondents yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsPanel />
          </TabsContent>


          <TabsContent value="cases">
            <Card className="mano-card">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-3 font-medium">Case</th>
                      <th className="p-3 font-medium">Respondent</th>
                      <th className="p-3 font-medium">Psychologist</th>
                      <th className="p-3 font-medium">Psychiatrist</th>
                      <th className="p-3 font-medium">Reviews</th>
                      <th className="p-3 font-medium">Status</th>
                      <th className="p-3 font-medium">Final outcome</th>
                    </tr>
                  </thead>
                  <tbody data-testid="cases-table">
                    {cases.map((c) => (
                      <tr key={c.case_id} className="border-t border-border/60">
                        <td className="p-3 font-mono text-[10px]">{c.case_id.slice(0, 8)}
                          {c.high_severity && <Badge className="ml-2 bg-destructive/10 text-destructive border border-destructive/30 text-[10px]">HIGH</Badge>}
                        </td>
                        <td className="p-3">
                          <div>{c.respondent_name}</div>
                          <div className="text-xs text-muted-foreground">{c.respondent_email}</div>
                        </td>
                        <td className="p-3 text-muted-foreground text-xs">{c.assigned_psychologist_id ? c.assigned_psychologist_id.slice(0, 12) + "…" : "-"}</td>
                        <td className="p-3 text-muted-foreground text-xs">{c.assigned_psychiatrist_id ? c.assigned_psychiatrist_id.slice(0, 12) + "…" : "-"}</td>
                        <td className="p-3">{c.review_count}/2</td>
                        <td className="p-3"><Badge variant="outline">{c.status.replace(/_/g, " ")}</Badge></td>
                        <td className="p-3">{c.final_outcome || "-"}</td>
                      </tr>
                    ))}
                    {cases.length === 0 && (
                      <tr><td colSpan="7" className="p-6 text-center text-muted-foreground">No cases yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="comparison">
            <ComparisonPanel
              comparison={comparison}
              loading={loadingComparison}
              onRefresh={loadComparison}
              agreementFilter={agreementFilter}
              setAgreementFilter={setAgreementFilter}
            />
          </TabsContent>

          <TabsContent value="organizations">
            <OrganizationsPanel
              insights={orgInsights}
              loading={loadingOrgs}
              onRefresh={loadOrgInsights}
              instruments={instruments}
            />
          </TabsContent>

          <TabsContent value="users">
            <Card className="mano-card">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-3 font-medium">Name</th>
                      <th className="p-3 font-medium">Email</th>
                      <th className="p-3 font-medium">Role</th>
                      <th className="p-3 font-medium">Cohort</th>
                      <th className="p-3 font-medium">Site</th>
                    </tr>
                  </thead>
                  <tbody data-testid="users-table">
                    {users.map((u) => (
                      <tr key={u.user_id} className="border-t border-border/60">
                        <td className="p-3">{u.name || "-"}<div className="text-[10px] text-muted-foreground">{u.auth_provider === "google" ? "Google sign-in" : u.auth_provider === "password" ? "Email + password" : "—"}</div></td>
                        <td className="p-3 text-muted-foreground">{u.email}</td>
                        <td className="p-3">
                          <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                            <SelectTrigger className="h-8 w-52" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="RESPONDENT">Respondent</SelectItem>
                              <SelectItem value="PSYCHOLOGIST">Psychologist</SelectItem>
                              <SelectItem value="PSYCHIATRIST">Psychiatrist</SelectItem>
                              <SelectItem value="CONTENT_ADMIN">Content Admin</SelectItem>
                              <SelectItem value="PLATFORM_ADMIN">Platform Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-3">
                          <Select value={u.cohort_id || "__none__"} onValueChange={(v) => changeUserScope(u.user_id, { cohort_id: v === "__none__" ? "" : v })}>
                            <SelectTrigger className="h-8 w-40" data-testid={`cohort-select-${u.email}`}><SelectValue placeholder="None" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">None</SelectItem>
                              {cohorts.map((c) => <SelectItem key={c.cohort_id} value={c.cohort_id}>{c.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-3">
                          <Select value={u.site_id || "__none__"} onValueChange={(v) => changeUserScope(u.user_id, { site_id: v === "__none__" ? "" : v })}>
                            <SelectTrigger className="h-8 w-40" data-testid={`site-select-${u.email}`}><SelectValue placeholder="None" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">None</SelectItem>
                              {sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
            <p className="text-xs text-muted-foreground mt-3">
              Users appear here after their first Google sign-in. Elevate a clinician's role before assigning cases to them, and assign cohorts/sites to scope which instruments they see.
            </p>
          </TabsContent>

          <TabsContent value="scope">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="mano-card">
                <CardContent className="p-6">
                  <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2">
                    <Layers size={16} /> Cohorts
                  </h3>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Input value={newCohort.name} onChange={(e) => setNewCohort({ ...newCohort, name: e.target.value })}
                      placeholder="Cohort name (e.g., 'Corporate Wellness Q1')" data-testid="cohort-input"
                      onKeyDown={(e) => e.key === "Enter" && addCohort()} />
                    <Button onClick={addCohort} className="rounded-full" data-testid="cohort-add-btn">
                      <Plus size={14} /> Add
                    </Button>
                  </div>
                  <div className="space-y-2" data-testid="cohorts-list">
                    {cohorts.length === 0 && <div className="text-sm text-muted-foreground">No cohorts yet.</div>}
                    {cohorts.map((c) => (
                      <div key={c.cohort_id} className="flex items-center justify-between rounded-lg border border-border/60 p-3">
                        <div>
                          <div className="text-sm font-medium">{c.name}</div>
                          {c.description && <div className="text-xs text-muted-foreground">{c.description}</div>}
                        </div>
                        <button onClick={() => removeCohort(c.cohort_id)} aria-label={`remove ${c.name}`}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                          data-testid={`cohort-remove-${c.name}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="mano-card">
                <CardContent className="p-6">
                  <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2">
                    <Layers size={16} /> Sites
                  </h3>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Input value={newSite.name} onChange={(e) => setNewSite({ ...newSite, name: e.target.value })}
                      placeholder="Site name (e.g., 'Bengaluru Clinic')" data-testid="site-input"
                      onKeyDown={(e) => e.key === "Enter" && addSite()} />
                    <Button onClick={addSite} className="rounded-full" data-testid="site-add-btn">
                      <Plus size={14} /> Add
                    </Button>
                  </div>
                  <div className="space-y-2" data-testid="sites-list">
                    {sites.length === 0 && <div className="text-sm text-muted-foreground">No sites yet.</div>}
                    {sites.map((s) => (
                      <div key={s.site_id} className="flex items-center justify-between rounded-lg border border-border/60 p-3">
                        <div>
                          <div className="text-sm font-medium">{s.name}</div>
                          {s.description && <div className="text-xs text-muted-foreground">{s.description}</div>}
                        </div>
                        <button onClick={() => removeSite(s.site_id)} aria-label={`remove ${s.name}`}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                          data-testid={`site-remove-${s.name}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Assign users to a cohort/site under <span className="font-medium">Users & Roles</span>. Assign scope to an instrument in the Instrument Builder. Empty scope on an instrument = global visibility.
            </p>
          </TabsContent>

          <TabsContent value="consent">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="mano-card">
                <CardContent className="p-6">
                  <h3 className="font-display font-semibold text-lg mb-4">Existing versions</h3>
                  <div className="space-y-3">
                    {consentVersions.map((c) => (
                      <div key={c.consent_id} className="rounded-lg border border-border/60 p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">v{c.version} · {c.language?.toUpperCase()}</div>
                            <div className="text-sm text-muted-foreground">{c.title}</div>
                          </div>
                          {c.active ? <Badge className="bg-primary/10 text-primary border border-primary/30">Active</Badge> : <Badge variant="outline">Archived</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card className="mano-card">
                <CardContent className="p-6">
                  <h3 className="font-display font-semibold text-lg mb-4">Publish new version</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Version</Label>
                        <Input value={newConsent.version} onChange={(e) => setNewConsent({ ...newConsent, version: e.target.value })}
                          placeholder="1.1.0" data-testid="consent-new-version" />
                      </div>
                      <div>
                        <Label>Language</Label>
                        <Input value={newConsent.language} onChange={(e) => setNewConsent({ ...newConsent, language: e.target.value })}
                          data-testid="consent-new-language" />
                      </div>
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input value={newConsent.title} onChange={(e) => setNewConsent({ ...newConsent, title: e.target.value })}
                        data-testid="consent-new-title" />
                    </div>
                    <div>
                      <Label>Body</Label>
                      <Textarea rows={6} value={newConsent.body} onChange={(e) => setNewConsent({ ...newConsent, body: e.target.value })}
                        data-testid="consent-new-body" />
                    </div>
                    <Button onClick={publishConsent} className="rounded-full mano-lift" data-testid="consent-publish-btn">
                      <Plus size={14} /> Publish
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="professions">
            <Card className="mano-card">
              <CardContent className="p-6">
                <div className="flex gap-3 mb-6">
                  <Input value={newProfession} onChange={(e) => setNewProfession(e.target.value)}
                    placeholder="Add new profession…" onKeyDown={(e) => e.key === "Enter" && addProfession()}
                    data-testid="profession-input" />
                  <Button onClick={addProfession} className="rounded-full" data-testid="profession-add-btn">
                    <Plus size={14} /> Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="profession-list">
                  {professions.map((p) => (
                    <span key={p} className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-card px-3 py-1 text-sm">
                      {p}
                      <button onClick={() => removeProfession(p)} className="text-muted-foreground hover:text-destructive transition-colors duration-150"
                        aria-label={`remove ${p}`} data-testid={`profession-remove-${p}`}>
                        <Trash2 size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <div className="grid gap-4">
              <Card className="mano-card">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-6">
                    <div className="max-w-xl">
                      <h3 className="font-display font-semibold text-lg">Show results to respondents</h3>
                      <p className="text-sm text-muted-foreground mt-2">
                        When disabled, respondents see a completion screen without scores. Use this when clinical review must precede any interpretation.
                      </p>
                    </div>
                    <Switch checked={showResults} onCheckedChange={toggleResults} data-testid="setting-show-results" />
                  </div>
                </CardContent>
              </Card>
              <Card className="mano-card">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-6">
                    <div className="max-w-xl">
                      <h3 className="font-display font-semibold text-lg">Psychiatrist tie-breaker</h3>
                      <p className="text-sm text-muted-foreground mt-2">
                        When enabled, the psychiatrist's outcome is used automatically when the two independent reviews disagree. When disabled, a case with disagreeing outcomes is flagged for admin resolution.
                      </p>
                    </div>
                    <Switch checked={tieBreaker} onCheckedChange={toggleTieBreaker} data-testid="setting-tiebreaker" />
                  </div>
                </CardContent>
              </Card>
              <Card className="mano-card">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div className="max-w-xl">
                      <h3 className="font-display font-semibold text-lg">Weekly activity digest</h3>
                      <p className="text-sm text-muted-foreground mt-2">
                        Send a one-off digest email to your inbox summarising new respondents, cases, finalized reviews, and audit events over the past 7 days.
                      </p>
                    </div>
                    <Button onClick={sendDigest} disabled={sendingDigest}
                      className="rounded-full mano-lift" data-testid="setting-digest-btn">
                      {sendingDigest ? "Sending…" : "Send digest now"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="audit">
            <Card className="mano-card">
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-3 font-medium">Timestamp</th>
                      <th className="p-3 font-medium">Actor</th>
                      <th className="p-3 font-medium">Role</th>
                      <th className="p-3 font-medium">Action</th>
                      <th className="p-3 font-medium">Target</th>
                    </tr>
                  </thead>
                  <tbody data-testid="audit-table">
                    {audit.map((a) => (
                      <tr key={a.audit_id} className="border-t border-border/60">
                        <td className="p-3 text-muted-foreground whitespace-nowrap">{(a.timestamp || "").replace("T", " ").slice(0, 19)}</td>
                        <td className="p-3">{a.actor_id?.slice(0, 12)}…</td>
                        <td className="p-3"><Badge variant="outline">{a.actor_role}</Badge></td>
                        <td className="p-3">{a.action}</td>
                        <td className="p-3 text-muted-foreground">{a.target || "-"}</td>
                      </tr>
                    ))}
                    {audit.length === 0 && (
                      <tr><td colSpan="5" className="p-6 text-center text-muted-foreground">No audit events yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <InstrumentBuilder
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        initial={builderInitial}
        cohorts={cohorts}
        sites={sites}
        onSaved={loadAll}
      />
    </div>
  );
}
